import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthPageLayout from '../components/AuthPageLayout';
import PasswordInput from '../components/PasswordInput';
import { apiUrl } from '../lib/consts';

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const { acceptInvite } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [inviteState, setInviteState] = useState<'checking' | 'ready' | 'expired'>('checking');

  // Validate the invite up front so a reclicked, already-used link goes straight to login.
  useEffect(() => {
    if (!token) {
      setError('Invalid invitation token');
      setInviteState('ready');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/users/invite/${token}`);
        const data = res.ok ? await res.json() : { status: 'invalid' };
        if (cancelled) return;
        if (data.status === 'invalid') {
          // Used invites clear their token on accept, so they land here.
          navigate('/login', { replace: true });
          return;
        }
        setInviteState(data.status === 'expired' ? 'expired' : 'ready');
      } catch {
        // Network error: fail open; the submit path still validates the token.
        if (!cancelled) setInviteState('ready');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  useEffect(() => {
    if (success) {
      const redirectTo = searchParams.get('redirect') || '/';
      const timer = setTimeout(() => {
        navigate(redirectTo, { replace: true });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setError('Invalid invitation token');
      return;
    }

    setLoading(true);

    try {
      await acceptInvite({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invite');
    } finally {
      setLoading(false);
    }
  };

  if (inviteState === 'checking') {
    return (
      <AuthPageLayout title="Accept Invitation" description="Checking your invitation…">
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      </AuthPageLayout>
    );
  }

  if (inviteState === 'expired') {
    return (
      <AuthPageLayout
        title="Invitation Expired"
        description="This invitation is no longer valid. Please ask an administrator to send a new one."
        footer={
          <p className="text-coolslate">
            Already have an account?{' '}
            <Link to="/login" className="text-brand hover:text-brand-dark font-medium">
              Sign in
            </Link>
          </p>
        }
      >
        <div />
      </AuthPageLayout>
    );
  }

  if (success) {
    return (
      <AuthPageLayout title="Account Activated!" description="Welcome to Navaris!">
        <div className="flex justify-center">
          <CheckCircle className="w-16 h-16 text-state-success" />
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Accept Invitation"
      description="Set your password to activate your account"
      error={error}
      footer={
        <p className="text-coolslate">
          Already have an account?{' '}
          <Link to="/login" className="text-brand hover:text-brand-dark font-medium">
            Sign in
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <PasswordInput
          id="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
          minLength={8}
          showPassword={showPassword}
          onToggleShow={() => setShowPassword(!showPassword)}
        />

        <PasswordInput
          id="confirmPassword"
          label="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          required
          showPassword={showPassword}
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Activating...
            </>
          ) : (
            'Activate Account'
          )}
        </button>
      </form>
    </AuthPageLayout>
  );
}
